import { useEffect } from 'react';

import type React from 'react';

/**
 * Hook to detect accessibility-focused interaction events outside of specified element(s)
 *
 * @description
 * This hook provides comprehensive interaction detection with support for:
 * - Mouse clicks (always enabled)
 * - Touch events (enabled by default)
 * - Scroll/touchmove events (optional)
 * - Escape key (optional)
 * - Multiple refs support
 *
 * @example
 * // Basic usage with single ref
 * const ref = useRef(null);
 * useAccessibilityEvent(ref, () => setOpen(false));
 *
 * @example
 * // With multiple refs
 * const modalRef = useRef(null);
 * const buttonRef = useRef(null);
 * useAccessibilityEvent([modalRef, buttonRef], () => setOpen(false));
 *
 * @example
 * // With options
 * useAccessibilityEvent(ref, handleClose, {
 *   enabled: isOpen,
 *   detectTouch: true,
 *   detectScroll: false,
 *   detectEscape: true,
 * });
 *
 * @note
 * **When to use which hook:**
 * - `useOutsideClick`: For simple dropdown menus (fixed 2 refs: dropdown + input)
 * - `useAccessibilityEvent`: For flexible components (modals, popovers, tooltips) with:
 *   - Single or multiple refs
 *   - Touch event support
 *   - Optional keyboard/scroll detection
 *   - Enable/disable toggle
 */

type UseAccessibilityEventOptions = {
  /** Enable or disable the hook */
  enabled?: boolean;
  /** Detect scroll/touchmove events */
  detectScroll?: boolean;
  /** Detect touch events (enabled by default) */
  detectTouch?: boolean;
  /** Detect Escape key press */
  detectEscape?: boolean;
};

function useAccessibilityEvent(
  refs: React.RefObject<HTMLElement> | React.RefObject<HTMLElement>[],
  handler: () => void,
  options: UseAccessibilityEventOptions = {},
) {
  const {
    enabled = true,
    detectScroll = false,
    detectTouch = true,
    detectEscape = false,
  } = options;

  useEffect(() => {
    if (!enabled) return;

    const refsArray = Array.isArray(refs) ? refs : [refs];

    function handleInteraction(event: MouseEvent | TouchEvent) {
      const target = event.target as Node;

      // Check if click/touch is outside all provided refs
      const isOutside = refsArray.every((ref) => ref.current && !ref.current.contains(target));

      if (isOutside) {
        handler();
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        handler();
      }
    }

    // Always listen for mouse events
    document.addEventListener('mousedown', handleInteraction);

    // Optionally listen for touch events
    if (detectTouch) {
      document.addEventListener('touchstart', handleInteraction);
    }

    // Optionally listen for scroll/touch move events
    if (detectScroll) {
      document.addEventListener('touchmove', handleInteraction);
    }

    // Optionally listen for escape key
    if (detectEscape) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleInteraction);
      if (detectTouch) {
        document.removeEventListener('touchstart', handleInteraction);
      }
      if (detectScroll) {
        document.removeEventListener('touchmove', handleInteraction);
      }
      if (detectEscape) {
        document.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [refs, handler, enabled, detectScroll, detectTouch, detectEscape]);
}

export default useAccessibilityEvent;
